import { trpc } from '@/config/trpc'
import env from '@/config/env'
import { Button } from '@/components/ui/shadcn/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/shadcn/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs'
import { ExternalLink, Trash } from 'lucide-react'
import { toast } from 'sonner'
import { CopyBlock, CopyField } from '@/components/developer/copy-field'

const MCP_URL = `${env.VITE_API_URL}/mcp`
const DOCS_URL = `${env.VITE_API_URL}/docs`

const CLAUDE_CODE_SNIPPET = `claude mcp add --transport http cassi ${MCP_URL}`

const JSON_CONFIG_SNIPPET = JSON.stringify(
  { mcpServers: { cassi: { type: 'http', url: MCP_URL } } },
  null,
  2,
)

// MCP (Model Context Protocol) connections an AI agent has been authorized
// to make on the user's behalf. Distinct from API keys: each row here is an
// OAuth-issued token bound to one brand + one MCP client.
export function AccountMcpPage() {
  const utils = trpc.useUtils()
  const { data: connections, isLoading } = trpc.mcpToken.list.useQuery()
  const { mutateAsync: revoke } = trpc.mcpToken.revoke.useMutation()

  const handleRevoke = async (id: number, label: string) => {
    if (!confirm(`Revoke "${label}"? The agent will lose access immediately.`)) return
    try {
      await revoke({ id })
      await utils.mcpToken.list.invalidate()
      toast.success('Connection revoked')
    } catch (e) {
      toast.error('Failed to revoke', {
        description: e instanceof Error ? e.message : 'Unknown error',
      })
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl sm:text-2xl">Connect an AI agent</CardTitle>
          <CardDescription>
            Point Claude, ChatGPT, or any MCP client at CASSI and it can work inside your brand:
            read the brief, see what has performed, draft posts, and schedule them. Add the server
            URL below, then approve the connection and pick a brand when your client opens the CASSI
            consent screen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <p className="text-sm font-medium">Server URL</p>
            <CopyField value={MCP_URL} label="MCP server URL" />
          </div>

          <Tabs defaultValue="claude-code">
            <TabsList>
              <TabsTrigger value="claude-code">Claude Code</TabsTrigger>
              <TabsTrigger value="desktop">Claude Desktop</TabsTrigger>
              <TabsTrigger value="other">Other clients</TabsTrigger>
            </TabsList>

            <TabsContent value="claude-code" className="space-y-3 pt-3">
              <p className="text-muted-foreground text-sm">Run this in your terminal:</p>
              <CopyBlock value={CLAUDE_CODE_SNIPPET} label="Claude Code command" />
              <p className="text-muted-foreground text-sm">
                Then run <span className="text-foreground font-mono">/mcp</span> inside Claude Code
                and authenticate.
              </p>
            </TabsContent>

            <TabsContent value="desktop" className="space-y-3 pt-3">
              <p className="text-muted-foreground text-sm">
                Settings → Connectors → Add custom connector, and paste the server URL above. Claude
                Desktop handles the sign-in from there.
              </p>
            </TabsContent>

            <TabsContent value="other" className="space-y-3 pt-3">
              <p className="text-muted-foreground text-sm">
                Most clients accept a JSON config in this shape:
              </p>
              <CopyBlock value={JSON_CONFIG_SNIPPET} label="MCP config" />
              <p className="text-muted-foreground text-sm">
                CASSI speaks streamable HTTP with OAuth 2.1. There is no key to paste: your client
                discovers the sign-in flow from the server URL.
              </p>
            </TabsContent>
          </Tabs>

          <Button variant="outline" asChild>
            <a href={DOCS_URL} target="_blank" rel="noreferrer">
              API reference
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Active connections</CardTitle>
          <CardDescription>
            AI agents you've authorized to act on a specific brand. Revoke at any time.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="text-muted-foreground text-sm">Loading…</p>}
          {connections && connections.length === 0 && (
            <p className="text-muted-foreground text-sm">
              No active connections yet. Follow the steps above to add one.
            </p>
          )}
          {connections && connections.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="pb-2 text-left font-normal">Client</th>
                  <th className="pb-2 text-left font-normal">Brand</th>
                  <th className="pb-2 text-left font-normal">Last used</th>
                  <th className="pb-2 text-right font-normal">Actions</th>
                </tr>
              </thead>
              <tbody>
                {connections.map((c) => (
                  <tr key={c.id} className="border-border/60 border-t first:border-t-0">
                    <td className="py-2 text-left">{c.clientName}</td>
                    <td className="text-muted-foreground py-2 text-left">{c.brandName}</td>
                    <td className="text-muted-foreground py-2 text-left">
                      {c.lastUsedAt ? new Date(c.lastUsedAt).toLocaleString() : 'never'}
                    </td>
                    <td className="py-2 text-right">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Revoke ${c.clientName}`}
                        onClick={() => handleRevoke(c.id, c.clientName)}>
                        <Trash className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
